import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {safelyGetFilterByKey} from 'nutshell-core/filter';
import type {FilterObject} from 'nutshell-core/types';

import {LAST_UPDATED_VALUE_OPTIONS} from 'shells/control-bar/constants';
import {ExpandedFilterBar} from 'shells/control-bar/expanded-filter-bar';
import {getArchivedValue, getLastUpdatedValue} from 'shells/control-bar/helpers';
import {ListPageControlBar} from 'shells/control-bar/list-page-control-bar';
import {ViewBoardIcon as BoardIcon} from 'shells/icon/svg/view-board-icon';
import {ViewListIcon as ListIcon} from 'shells/icon/svg/view-list-icon';
import {TabbedContent} from 'shells/layout/tabbed-content';
import {Link} from 'shells/link/link';
import {PaginatedView} from 'shells/list-view/paginated-view';

import type {
    FormStubFragment as FormStub,
    FormDuplicateInputFieldInput,
} from 'nutshell-graphql-types';

import {writeLocalStorage} from '../../../util/localStorage';

import {GetSessionUser} from '../../core/get-session-user';
import {checkIsFiltering} from '../../email-marketing/email-marketing-helpers';
import {PAGE_CONTAINER_CONTENT_ID, PageContainer, PageTitleBar} from '../../layout';
import {TitleBarLimits} from '../../limits/title-bar-limits';

import {FormListQuery} from '../graphql/graphql-wrappers/form-list-query';
import {FormMutations} from '../graphql/graphql-wrappers/form-mutations';
import {getFormListQueryVariables} from '../graphql/graphql-wrappers/helpers';

import {FormsGrid} from './forms-grid';
import {FormsTable} from './forms-table';

type OwnProps = ContextRouter & {
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    urlParams: any;
    getUpdatedUrl: (arg1: any) => string;
    onUpdateFilters: (arg1: FilterObject) => void;
    onRemoveAllFilters: () => void;
};

type Props = OwnProps & {
    forms: FormStub[];
    totalCount: number;
    onDeleteForm: (arg1: string) => Promise<any>;
    onRestoreForm: (arg1: string) => Promise<any>;
    onDuplicateForm: (arg1: FormDuplicateInputFieldInput) => Promise<any>;
    isLoading: boolean;
    isFirstLoad: boolean;
    error: string | null | undefined;
    searchValue: string;
    userId: string;
    onParamChange: (arg1: any, arg2: boolean) => any;
    activeFilters: FilterObject[];
    urlParams: any;
    getUpdatedUrl: (arg1: any) => string;
    onUpdateFilters: (arg1: FilterObject) => void;
    hasNextPage: boolean;
    onFetchMoreRows: () => any;
};

const FORM_VIEW_SEARCH = 'FORM_VIEW_SEARCH';

function FormsListPageComponent(props: Props) {
    const selectedTab = props.urlParams.view || 'grid';

    React.useEffect(() => {
        writeLocalStorage(FORM_VIEW_SEARCH, props.location.search);
    }, [props.location.search]);

    const isFilteringOnCreatorUserId = Boolean(
        // @ts-expect-error
        props.activeFilters.find((filter) => {
            return Object.keys(filter)[0] === 'creatorUserId';
        })
    );

    const isShowingArchived = safelyGetFilterByKey(props.activeFilters, 'archived') === 'true';

    const isFiltering = checkIsFiltering(props.activeFilters, props.searchValue);

    const tabOptions = [
        {
            label: 'Board',
            id: 'board',
            icon: BoardIcon,
            href: props.getUpdatedUrl({
                view: 'board',
            }),
            component: (
                <PaginatedView
                    scrollContainerId={PAGE_CONTAINER_CONTENT_ID}
                    hasNextPage={props.hasNextPage}
                    // only want to show infinite load spinner if not the first load/filtering
                    isLoading={props.isLoading && !props.isFirstLoad && !isFiltering}
                    onFetchMoreRows={props.onFetchMoreRows}
                    renderBody={
                        <FormsGrid
                            forms={props.forms}
                            isLoading={props.isLoading}
                            isFiltering={isFiltering}
                            isShowingArchived={isShowingArchived}
                            error={props.error}
                            onDeleteForm={props.onDeleteForm}
                            onRestoreForm={props.onRestoreForm}
                            onDuplicateForm={props.onDuplicateForm}
                            locationSearch={props.location.search}
                        />
                    }
                />
            ),
        },
        {
            label: 'Table',
            id: 'table',
            icon: ListIcon,
            href: props.getUpdatedUrl({
                view: 'table',
            }),
            component: (
                <PaginatedView
                    hasNextPage={props.hasNextPage}
                    isLoading={props.isLoading}
                    onFetchMoreRows={props.onFetchMoreRows}
                    renderBody={
                        <FormsTable
                            forms={props.forms}
                            isLoading={props.isLoading}
                            isFiltering={isFiltering}
                            isShowingArchived={isShowingArchived}
                            error={props.error}
                            sortString={props.urlParams.sort ? props.urlParams.sort : ''}
                            onChangeSort={(newSortString) => {
                                props.onParamChange({sort: newSortString}, true);
                            }}
                            locationSearch={props.location.search}
                        />
                    }
                />
            ),
        },
    ];

    return (
        <PageContainer
            title='Nutshell forms'
            pageTitleBar={
                <PageTitleBar
                    title='Nutshell forms'
                    subheaderPrimaryText='Design and embed forms to collect leads from your website'
                    titleBadge={<TitleBarLimits type='FORMS' />}
                    buttons={
                        <Link
                            size='big'
                            variant='button-primary'
                            // @ts-expect-error
                            as={RoutedLink}
                            to={{
                                // GREP #/create/forms
                                hash: '#/create/forms',
                                search: props.location.search,
                                state: {modal: true},
                            }}
                            replace={true}
                        >
                            Create new form
                        </Link>
                    }
                />
            }
            controlBar={
                <ListPageControlBar
                    archiveTab={{
                        isActive: isShowingArchived,
                        onChange: () => {
                            props.onUpdateFilters({
                                archived: isShowingArchived ? null : true,
                            });
                        },
                    }}
                    tabs={{
                        selectedTab: selectedTab,
                        options: tabOptions,
                    }}
                    leftSection={
                        <ExpandedFilterBar
                            lastUpdatedFilterProps={{
                                lastUpdatedFilter: safelyGetFilterByKey(
                                    props.activeFilters,
                                    'lastUpdated'
                                ),
                                // @ts-expect-error
                                lastUpdatedOptions: LAST_UPDATED_VALUE_OPTIONS,
                            }}
                            justMineFilterProps={{
                                isFilteringOnCreatorUserId: isFilteringOnCreatorUserId,
                                userId: props.userId,
                            }}
                            searchFilterProps={{
                                searchValue: props.searchValue,
                                onUpdateSearch: (newSearchValue) => {
                                    props.onParamChange({q: newSearchValue}, true);
                                },
                            }}
                            isFiltering={isFiltering}
                            noun='form'
                            count={props.totalCount}
                            onUpdateFilters={props.onUpdateFilters}
                            onRemoveAllFilters={props.onRemoveAllFilters}
                            activeFilters={props.activeFilters}
                        />
                    }
                />
            }
        >
            {/* @ts-expect-error */}
            <TabbedContent selectedTab={selectedTab} tabOptions={tabOptions} />
        </PageContainer>
    );
}

export function FormsListPage(props: OwnProps) {
    // We've hardcoded the server's sort for now - table sorting is currently
    // handled on the client and stored in the URL
    const sortValue = 'MODIFIED_TIME_DESC';
    const searchValue = props.urlParams.q || '';
    const isArchived = getArchivedValue(props.activeFilters);

    // Rough calculation of how many rows fit on the screen
    const limit = Math.ceil(window.innerHeight / 33);

    const queryVariables = getFormListQueryVariables(isArchived, sortValue, searchValue, limit);

    const isFilteringOnCreatorUserId = Boolean(
        // @ts-expect-error
        props.activeFilters.find((filter) => {
            return Object.keys(filter)[0] === 'creatorUserId';
        })
    );

    const lastUpdatedTime = getLastUpdatedValue(props.activeFilters);

    return (
        <GetSessionUser>
            {/* @ts-expect-error */}
            {({user}) => {
                return user ? (
                    <FormListQuery
                        isArchived={isArchived}
                        sortOrder={sortValue}
                        limit={limit}
                        searchQuery={searchValue}
                        creatorUserId={isFilteringOnCreatorUserId ? user.id : undefined}
                        lastUpdatedTime={lastUpdatedTime}
                    >
                        {({
                            forms,
                            totalCount,
                            isLoading,
                            isFirstLoad,
                            error,
                            fetchMoreRows,
                            hasNextPage,
                        }) => (
                            <>
                                <FormMutations>
                                    {({onDelete, onRestore, onDuplicate}) => (
                                        <FormsListPageComponent
                                            {...props}
                                            forms={forms}
                                            queryVariables={queryVariables}
                                            totalCount={totalCount}
                                            isLoading={isLoading}
                                            isFirstLoad={isFirstLoad}
                                            error={error}
                                            // @ts-expect-error
                                            onDeleteForm={(formId) => {
                                                return onDelete(formId, queryVariables);
                                            }}
                                            // @ts-expect-error
                                            onRestoreForm={(formId) => {
                                                return onRestore(formId, queryVariables);
                                            }}
                                            onDuplicateForm={(
                                                input: FormDuplicateInputFieldInput
                                            ) => onDuplicate(input, queryVariables)}
                                            searchValue={searchValue}
                                            userId={user.id}
                                            onFetchMoreRows={fetchMoreRows}
                                            hasNextPage={hasNextPage}
                                        />
                                    )}
                                </FormMutations>
                            </>
                        )}
                    </FormListQuery>
                ) : null;
            }}
        </GetSessionUser>
    );
}
